if (!customElements.get('variant-selects')) {

  /**
   *  @class
   *  @function VariantSelects
   */
  class VariantSelects extends HTMLElement {
    constructor() {
      super();
      this.selectedVariantGroup = "";
      this.sticky = this.dataset.sticky;
      this.updateUrl = this.dataset.updateUrl === 'true';
      this.isDisabledFeature = this.dataset.isDisabled;
      
      // NO registrar el event listener aquí - se registrará en connectedCallback
      // Esto asegura que el listener se registre después de que el elemento esté completamente inicializado
      this._changeHandler = this.onVariantChange.bind(this);
      
      // Log para verificar que el constructor se ejecuta
      const context = this.closest('#Product-Drawer-Content') ? 'quick-view' : 'product-page';
      console.log(`[variant-selects] constructor called (${context}):`, {
        section: this.dataset.section,
        hasDataset: !!this.dataset,
        hasChangeHandler: !!this._changeHandler
      });
      
      // Only include other variant-selects from the same product/section
      // This prevents mixing data between different products
      const currentSectionId = this.dataset.section;
      this.other = Array.from(document.querySelectorAll('variant-selects')).filter((selector) => {
        return selector != this && selector.dataset.section === currentSectionId;
      });
      this.initializeReferences();
    }
    
    initializeReferences() {
      this.productWrapper = this.closest('.thb-product-detail');
      if (this.productWrapper) {
        this.productSlider = this.productWrapper.querySelector('.product-images') || 
                           this.productWrapper.querySelector('.product-quick-images');
        this.thumbnails = this.productWrapper.querySelector('.product-thumbnail-container');
        if (this.productSlider) {
          // Verificar tanto hideVariants como hide-variants (kebab-case)
          const hideVariantsAttr = this.productSlider.dataset.hideVariants || 
                                   this.productSlider.getAttribute('data-hide-variants');
          this.hideVariants = hideVariantsAttr === 'true' || hideVariantsAttr === true;
        }
      }
    }

    connectedCallback() {
      const context = this.closest('#Product-Drawer-Content') ? 'quick-view' : 'product-page';
      console.log(`[variant-selects] connectedCallback called (${context}):`, {
        section: this.dataset.section,
        hasProductWrapper: !!this.productWrapper,
        hasProductSlider: !!this.productSlider,
        hasVariations: !!this.querySelector('.variations'),
        hasChangeHandler: !!this._changeHandler
      });
      
      // Asegurar que las referencias estén inicializadas
      if (!this.productWrapper || !this.productSlider) {
        console.log(`[variant-selects] Re-initializing references in connectedCallback (${context})`);
        this.initializeReferences();
        console.log(`[variant-selects] After initializeReferences (${context}):`, {
          hasProductWrapper: !!this.productWrapper,
          hasProductSlider: !!this.productSlider,
          hideVariants: this.hideVariants
        });
      }
      
      // Registrar el event listener para cambios de variante
      // Esto es crítico: el listener debe registrarse en connectedCallback, no en constructor
      // porque en modo incógnito, cuando el elemento se inyecta dinámicamente,
      // el constructor puede ejecutarse antes de que el DOM esté completamente listo
      if (this._changeHandler && !this._changeListenerRegistered) {
        console.log(`[variant-selects] Registering change event listener (${context})`);
        
        // Usar event delegation con capture para asegurar que capture todos los eventos
        // incluso en modo incógnito donde el bubbling puede no funcionar correctamente
        this.addEventListener('change', this._changeHandler, true); // true = useCapture
        this._changeListenerRegistered = true;
        
        // Verificar que el listener se registró correctamente
        const variations = this.querySelector('.variations');
        if (variations) {
          const allInputs = variations.querySelectorAll('input[type="radio"], input[type="checkbox"], select');
          console.log(`[variant-selects] Event listener registered (${context}):`, {
            hasVariations: !!variations,
            totalInputs: allInputs.length,
            firstInput: allInputs[0] ? {
              type: allInputs[0].type || allInputs[0].tagName,
              name: allInputs[0].name,
              value: allInputs[0].value
            } : null
          });
          
          // CRÍTICO: Registrar listeners directamente en los inputs como fallback
          // Esto asegura que los eventos se capturen incluso si el bubbling falla
          // Especialmente importante para Edge y modo incógnito
          console.log(`[variant-selects] Registering direct listeners on inputs (${context})`);
          allInputs.forEach((input, index) => {
            // Solo registrar si no tiene ya un listener
            if (!input.hasAttribute('data-change-listener-registered')) {
              const directHandler = (e) => {
                console.log(`[variant-selects] Direct input change event (${context}):`, {
                  inputName: e.target.name,
                  inputValue: e.target.value,
                  inputType: e.target.type,
                  inputId: e.target.id
                });
                // Llamar al handler principal
                if (this._changeHandler) {
                  this._changeHandler(e);
                } else if (typeof this.onVariantChange === 'function') {
                  this.onVariantChange(e);
                }
              };
              input.addEventListener('change', directHandler);
              input.setAttribute('data-change-listener-registered', 'true');
            }
          });
          console.log(`[variant-selects] Direct listeners registered on ${allInputs.length} inputs (${context})`);
        }
      } else if (this._changeListenerRegistered) {
        console.log(`[variant-selects] Change listener already registered (${context})`);
      } else {
        console.warn(`[variant-selects] No change handler available (${context})`);
      }
      
      console.log(`[variant-selects] Executing connectedCallback methods (${context})`);
      this.updateOptions();
      console.log(`[variant-selects] updateOptions completed (${context})`);
      
      this.updateMasterId();
      console.log(`[variant-selects] updateMasterId completed (${context})`);
      
      this.setDisabled();
      console.log(`[variant-selects] setDisabled completed (${context})`);
      
      // Delay setImageSet to ensure metafield images are loaded
      // Use requestAnimationFrame to wait for DOM to be ready
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.setImageSet();
          console.log(`[variant-selects] setImageSet completed (${context})`);
        }, 50);
      });
      
      console.log(`[variant-selects] connectedCallback finished (${context}):`, {
        imageSetName: this.imageSetName,
        imageSetIndex: this.imageSetIndex,
        currentVariant: this.currentVariant ? { id: this.currentVariant.id, title: this.currentVariant.title } : null,
        changeListenerRegistered: this._changeListenerRegistered
      });
    }
    
    disconnectedCallback() {
      // Clean up timeouts when element is removed from DOM
      if (this._imageSetTimeout) {
        clearTimeout(this._imageSetTimeout);
        this._imageSetTimeout = null;
      }
      // Clear cached data when element is disconnected
      this.variantData = null;
      this._cachedSectionId = null;
    }

    onVariantChange(event) {
      const context = this.closest('#Product-Drawer-Content') ? 'quick-view' : 'product-page';
      console.log(`[variant-selects] onVariantChange called (${context}):`, {
        section: this.dataset.section,
        eventTarget: event?.target?.tagName,
        eventType: event?.type,
        targetValue: event?.target?.value,
        targetName: event?.target?.name
      });
      
      this.updateOptions();
      console.log(`[variant-selects] onVariantChange - updateOptions completed (${context})`);
      
      this.updateMasterId();
      console.log(`[variant-selects] onVariantChange - updateMasterId completed (${context})`);
      
      this.toggleAddButton(true, '', false);
      this.updatePickupAvailability();
      this.removeErrorMessage();
      this.updateVariantText();
      this.setDisabled();

      if (!this.currentVariant) {
        console.log(`[variant-selects] onVariantChange - No currentVariant (${context})`);
        this.toggleAddButton(true, '', true);
        this.setUnavailable();
      } else {
        console.log(`[variant-selects] onVariantChange - Updating media for variant (${context}):`, {
          variantId: this.currentVariant.id,
          variantTitle: this.currentVariant.title,
          featuredMediaId: this.currentVariant.featured_media?.id
        });
        this.updateMedia();
        if (this.updateUrl) {
          this.updateURL();
        }
        this.updateVariantInput();
        this.renderProductInfo();
        //this.updateShareUrl();
      }
      this.updateOther();
      dispatchCustomEvent('product:variant-change', {
        variant: this.currentVariant,
        sectionId: this.dataset.section
      });
      console.log(`[variant-selects] onVariantChange completed (${context})`);
    }

    updateOptions() {
      this.fieldsets = Array.from(this.querySelectorAll('fieldset'));
      this.options = [];
      this.option_keys = [];
      this.fieldsets.forEach((fieldset, i) => {
        if (!fieldset) return; // Skip if fieldset is null
        
        const selectElement = fieldset.querySelector('select');
        if (selectElement && selectElement.value !== null && selectElement.value !== undefined) {
          this.options.push(selectElement.value);
          if (selectElement.name) {
            this.option_keys.push(selectElement.name);
          }
        } else if (fieldset.querySelectorAll('input').length) {
          const checkedInput = fieldset.querySelector('input:checked');
          if (checkedInput && checkedInput.value !== null && checkedInput.value !== undefined) {
            this.options.push(checkedInput.value);
            const firstInput = fieldset.querySelector('input');
            if (firstInput && firstInput.name) {
              this.option_keys.push(firstInput.name);
            }
            console.log(checkedInput.dataset.option)
            if (checkedInput.dataset && checkedInput.dataset.option) {
              this.selectedVariantGroup = checkedInput.dataset.option;
            }
          }
        }
      });
      this.dataset.options = this.options;
    }
    
    updateVariantText() {
      const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    
      fieldsets.forEach((item, i) => {
        const allLabels = item.querySelectorAll('.form__label__value');
    
        allLabels.forEach(label => {
          const labelOption = label.getAttribute('data-option');
    
          if (item.dataset.splits === "true") {
            if (labelOption === this.selectedVariantGroup) {
              label.innerHTML = this.options[i];
            } else if (labelOption) {
              // Clear labels with a data-option that doesn't match
              label.innerHTML = '';
            }
            // Labels without data-option are untouched
          } else if (!labelOption) {
            // If splits is not true and no data-option, update as usual
            label.innerHTML = this.options[i];
          }
        });
      });
    }

    
    updateMasterId() {
      const variantData = this.getVariantData();
      if (!Array.isArray(variantData) || variantData.length === 0) {
        console.warn('[variant-selects] No variant data available');
        this.currentVariant = null;
        return;
      }
      
      this.currentVariant = variantData.find((variant) => {
        if (!variant || !variant.options || !Array.isArray(variant.options)) return false;
        return !variant.options.map((option, index) => {
          return this.options[index] === option;
        }).includes(false);
      });
      
      // Log para examinar la variante seleccionada
      console.log('[variant-selects] updateMasterId:', {
        context: this.closest('#Product-Drawer-Content') ? 'quick-view' : 'product-page',
        section: this.dataset.section,
        options: this.options,
        currentVariant: this.currentVariant ? {
          id: this.currentVariant.id,
          title: this.currentVariant.title,
          available: this.currentVariant.available,
          featured_media: this.currentVariant.featured_media ? {
            id: this.currentVariant.featured_media.id
          } : null,
          options: this.currentVariant.options
        } : null
      });
    }

    updateOther() {
      if (this.dataset.updateUrl === 'false') {
        return;
      }
      if (this.other.length) {
        // Validate that other variant-selects belong to the same product/section
        const currentSectionId = this.dataset.section;
        const validOther = this.other.find(selector => selector.dataset.section === currentSectionId);
        
        if (!validOther) {
          console.warn('[variant-selects] updateOther: No valid other variant-selects found for same section');
          return;
        }
        
        let fieldsets = validOther.querySelectorAll('fieldset'),
          fieldsets_array = Array.from(fieldsets);
        this.options.forEach((option, i) => {
          if (fieldsets_array[i] && option !== null && option !== undefined) {
            const selectElement = fieldsets_array[i].querySelector('select');
            if (selectElement) {
              selectElement.value = option;
            } else if (fieldsets_array[i].querySelectorAll('input').length) {
              const inputElement = fieldsets_array[i].querySelector(`input[value="${option}"]`);
              if (inputElement) {
                inputElement.checked = true;
              }
            }
          }
        });
        validOther.updateOptions();
        validOther.updateMasterId();
        validOther.updateVariantText();
        validOther.setDisabled();
      }
    }

    updateMedia() {
      if (!this.currentVariant) {
        console.log('[variant-selects] updateMedia: No currentVariant', {
          context: this.closest('#Product-Drawer-Content') ? 'quick-view' : 'product-page',
          section: this.dataset.section
        });
        return;
      }
      if (!this.currentVariant.featured_media) {
        console.log('[variant-selects] updateMedia: No featured_media', {
          context: this.closest('#Product-Drawer-Content') ? 'quick-view' : 'product-page',
          section: this.dataset.section,
          currentVariant: {
            id: this.currentVariant.id,
            title: this.currentVariant.title
          }
        });
        return;
      }
      
      // Re-inicializar referencias si no están disponibles
      if (!this.productSlider) {
        this.initializeReferences();
      }
      if (!this.productSlider) {
        console.log('[variant-selects] updateMedia: No productSlider', {
          context: this.closest('#Product-Drawer-Content') ? 'quick-view' : 'product-page',
          section: this.dataset.section
        });
        return;
      }
      
      let mediaId = `#Slide-${this.dataset.section}-${this.currentVariant.featured_media.id}`;
      let activeMedia = this.productSlider.querySelector(mediaId);
      
      // Si no se encuentra con el ID exacto, intentar buscar por data-media-id
      if (!activeMedia) {
        activeMedia = this.productSlider.querySelector(`[data-media-id="${this.dataset.section}-${this.currentVariant.featured_media.id}"]`);
      }

      console.log('[variant-selects] updateMedia:', {
        context: this.closest('#Product-Drawer-Content') ? 'quick-view' : 'product-page',
        section: this.dataset.section,
        currentVariant: {
          id: this.currentVariant.id,
          title: this.currentVariant.title,
          featured_media_id: this.currentVariant.featured_media.id
        },
        mediaId: mediaId,
        foundById: !!this.productSlider.querySelector(mediaId),
        foundByDataAttribute: !!this.productSlider.querySelector(`[data-media-id="${this.dataset.section}-${this.currentVariant.featured_media.id}"]`),
        activeMedia: activeMedia ? {
          id: activeMedia.id,
          mediaId: activeMedia.dataset.mediaId,
          hasIsActive: activeMedia.classList.contains('is-active')
        } : null,
        hasThumbnails: !!this.thumbnails
      });

      if (activeMedia) {
        if (this.thumbnails) {
          this.setActiveMediaSlider(mediaId, `#Thumb-${this.dataset.section}-${this.currentVariant.featured_media.id}`, this.productSlider);
        } else {
          this.setActiveMedia(activeMedia);
        }
      } else {
        console.warn('[variant-selects] updateMedia: Active media not found', {
          context: this.closest('#Product-Drawer-Content') ? 'quick-view' : 'product-page',
          section: this.dataset.section,
          mediaId: mediaId,
          allSlides: Array.from(this.productSlider.querySelectorAll('[data-media-id]')).map(slide => ({
            id: slide.id,
            mediaId: slide.dataset.mediaId
          }))
        });
      }
    }
    setActiveMedia(activeMedia) {
      if (!activeMedia || !this.productSlider) return;

      // Remover is-active de todas las imágenes (tanto product-images como product-quick-images)
      this.productSlider.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });

      // Filtrar imágenes por variante usando setImageSetMedia
      this.setImageSetMedia();

      activeMedia.classList.add('is-active');

      // Solo hacer prepend si el elemento tiene un parentElement
      if (activeMedia.parentElement) {
        activeMedia.parentElement.prepend(activeMedia);
      }

      if (!this.sticky) {
        window.setTimeout(() => {
          if (window.innerWidth > 1068) {
            let header_h = activeMedia.parentElement.offsetTop - parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')),
              scroll_obj = {
                left: 0,
                behavior: 'instant'
              };

            if (header_h > 0) {
              scroll_obj.top = header_h;
            }
            window.scrollTo(scroll_obj);
          }
          activeMedia.parentElement.scrollTo({
            left: 0,
            behavior: 'instant'
          });
        });
      }
    }
    setActiveMediaSlider(mediaId, thumbId, productSlider) {
      let flkty = Flickity.data(productSlider),
        activeMedia = productSlider.querySelector(mediaId);
      
      // Si no se encuentra con el ID exacto, intentar buscar por data-media-id
      if (!activeMedia && mediaId) {
        const mediaIdMatch = mediaId.match(/#Slide-([^-]+)-(.+)/);
        if (mediaIdMatch) {
          activeMedia = productSlider.querySelector(`[data-media-id="${mediaIdMatch[1]}-${mediaIdMatch[2]}"]`);
        }
      }
      
      // Si aún no se encuentra, retornar sin hacer nada
      if (!activeMedia) {
        console.warn('Active media not found:', mediaId);
        return;
      }

      if (flkty && this.hideVariants) {
        // Buscar tanto en product-images como en product-quick-images
        const initialSelected = productSlider.querySelector('.product-images__slide.is-initial-selected, .product-quick-images__slide.is-initial-selected');
        if (initialSelected) {
          initialSelected.classList.remove('is-initial-selected');
        }
        [].forEach.call(productSlider.querySelectorAll('.product-images__slide-item--variant, .product-quick-images__slide.product-images__slide-item--variant'), function (el) {
          el.classList.remove('is-active');
        });
        if (this.thumbnails) {
          if (this.thumbnails.querySelector('.product-thumbnail.is-initial-selected')) {
            this.thumbnails.querySelector('.product-thumbnail.is-initial-selected').classList.remove('is-initial-selected');
          }
          [].forEach.call(this.thumbnails.querySelectorAll('.product-images__slide-item--variant'), function (el) {
            el.classList.remove('is-active');
          });
        }

        activeMedia.classList.add('is-active');
        activeMedia.classList.add('is-initial-selected');

        this.setImageSetMedia();

        if (this.thumbnails) {
          let activeThumb = this.thumbnails.querySelector(thumbId);
          if (activeThumb) {
            activeThumb.classList.add('is-active');
            activeThumb.classList.add('is-initial-selected');
          }
        }

        if (typeof productSlider.reInit === 'function') {
          productSlider.reInit(this.imageSetIndex);
        }
        if (typeof productSlider.selectCell === 'function') {
          productSlider.selectCell(mediaId);
        }

      } else if (flkty) {
        if (typeof productSlider.selectCell === 'function') {
          productSlider.selectCell(mediaId);
        }
      }

    }

    updateURL() {
      if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
    }

    updateShareUrl() {
      const shareButton = document.getElementById(`Share-${this.dataset.section}`);
      if (!shareButton) return;
      shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
    }

    updateVariantInput() {
      // Buscar en el contexto global primero
      let productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment`);
      
      // Si no se encuentra, buscar en el contexto del quick view
      if (productForms.length === 0) {
        const quickViewContent = this.closest('#Product-Drawer-Content');
        if (quickViewContent) {
          productForms = quickViewContent.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment`);
        }
      }
      
      productForms.forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        if (input && this.currentVariant) {
          input.value = this.currentVariant.id;
          input.dispatchEvent(new Event('change', {
            bubbles: true
          }));
        }
      });
    }

    updatePickupAvailability() {
      const pickUpAvailability = document.querySelector('.pickup-availability-wrapper');

      if (!pickUpAvailability) return;

      if (this.currentVariant && this.currentVariant.available) {
        pickUpAvailability.fetchAvailability(this.currentVariant.id);
      } else {
        pickUpAvailability.removeAttribute('available');
        pickUpAvailability.innerHTML = '';
      }
    }

    removeErrorMessage() {
      const section = this.closest('section');
      if (!section) return;

      const productForm = section.querySelector('product-form');
      if (productForm) productForm.handleErrorMessage();
    }

    getSectionsToRender() {
      return [`price-${this.dataset.section}`, `price-${this.dataset.section}--sticky`, `product-image-${this.dataset.section}--sticky`, `inventory-${this.dataset.section}`, `sku-${this.dataset.section}`, `quantity-${this.dataset.section}`];
    }

    renderProductInfo(attempt = 0) {
      let sections = this.getSectionsToRender();
      const requestedVariantId = this.currentVariant.id;

      fetch(`${this.dataset.url}?variant=${requestedVariantId}&section_id=${this.dataset.section}`)
        .then((response) => {
          if (!response.ok) throw new Error(`product info ${response.status}`);
          return response.text();
        })
        .then((responseText) => {
          // Con un cambio rápido de talla o color, una respuesta lenta no debe pisar la de la variante elegida al final
          // (si no, el número de stock restante queda de la variante anterior)
          if (!this.currentVariant || this.currentVariant.id !== requestedVariantId) return;
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          sections.forEach((id) => {
            const destination = document.getElementById(id);
            const source = html.getElementById(id);

            if (source && destination) destination.innerHTML = source.innerHTML;

            const price = document.getElementById(id);
            const price_fixed = document.getElementById(id + '--sticky');

            if (price) price.classList.remove('visibility-hidden');
            if (price_fixed) price_fixed.classList.remove('visibility-hidden');

          });
          this.toggleAddButton(!this.currentVariant.available, window.theme.variantStrings.soldOut);

        })
        .catch(() => {
          // Un fallo de red o un límite de peticiones no debe dejar el stock y el precio de la variante anterior: se reintenta una vez
          if (attempt >= 1) {
            // Falló también el reintento: sin dato de esta variante no se muestra ningún «Only X left» (sería de otra talla)
            const inventory = document.getElementById(`inventory-${this.dataset.section}`);
            if (inventory && this.currentVariant && this.currentVariant.id === requestedVariantId) inventory.innerHTML = '';
            return;
          }
          setTimeout(() => {
            if (this.currentVariant && this.currentVariant.id === requestedVariantId) this.renderProductInfo(attempt + 1);
          }, 800);
        });
    }

    toggleAddButton(disable = true, text = false, modifyClass = true) {
      const productForm = document.getElementById(`product-form-${this.dataset.section}`);
      if (!productForm) {
        // Intentar buscar en el contexto del quick view
        const quickViewContent = this.closest('#Product-Drawer-Content');
        if (quickViewContent) {
          const formInQuickView = quickViewContent.querySelector(`#product-form-${this.dataset.section}`);
          if (formInQuickView) {
            const productTemplate = formInQuickView.closest('.product-form')?.getAttribute('template');
            const submitButtons = quickViewContent.querySelectorAll('.single-add-to-cart-button');
            if (submitButtons && submitButtons.length > 0) {
              this.toggleButtons(submitButtons, disable, text, productTemplate);
              return;
            }
          }
        }
        return;
      }

      const productTemplate = productForm.closest('.product-form').getAttribute('template');
      const submitButtons = document.querySelectorAll('.single-add-to-cart-button');

      if (!submitButtons || submitButtons.length === 0) return;
      
      this.toggleButtons(submitButtons, disable, text, productTemplate);
      
      if (!modifyClass) return;
    }
    
    toggleButtons(submitButtons, disable, text, productTemplate) {
      submitButtons.forEach((submitButton) => {
        const submitButtonText = submitButton.querySelector('.single-add-to-cart-button--text');

        if (!submitButtonText) return;

        if (disable) {
          submitButton.setAttribute('disabled', 'disabled');
          if (text) submitButtonText.textContent = text;
        } else {
          submitButton.removeAttribute('disabled');
          submitButton.classList.remove('loading');

          if (productTemplate?.includes('pre-order')) {
            submitButtonText.textContent = window.theme?.variantStrings?.preOrder || 'Pre-order';
          } else {
            submitButtonText.textContent = window.theme?.variantStrings?.addToCart || 'Add to cart';
          }
        }
      });
    }

    setUnavailable() {
      const submitButtons = document.querySelectorAll('.single-add-to-cart-button');
      const price = document.getElementById(`price-${this.dataset.section}`);
      const price_fixed = document.getElementById(`price-${this.dataset.section}--sticky`);
      // La combinación no existe: se quita el aviso de stock de la variante anterior («Only X left»)
      const inventory = document.getElementById(`inventory-${this.dataset.section}`);
      if (inventory) inventory.innerHTML = '';

      submitButtons.forEach((submitButton) => {
        const submitButtonText = submitButton.querySelector('.single-add-to-cart-button--text');
        if (!submitButton) return;
        submitButtonText.textContent = window.theme.variantStrings.unavailable;
        submitButton.classList.add('sold-out');
      });
      if (price) price.classList.add('visibility-hidden');
      if (price_fixed) price_fixed.classList.add('visibility-hidden');
    }

    setDisabled() {
      if (this.isDisabledFeature != 'true') {
        return;
      }
      const variant_data = this.getVariantData();


      if (variant_data && Array.isArray(variant_data)) {
        let selected_options = false;
        if (this.currentVariant && this.currentVariant.options && Array.isArray(this.currentVariant.options)) {
          selected_options = this.currentVariant.options.map((value, index) => {
            return {
              value,
              index: `option${index + 1}`
            };
          });
        } else {
          let found_option = variant_data.find(option => {
            return option && option.option1 === this.options[0];
          });
          if (found_option) {
            selected_options = [
              {
                "value": this.options[0],
                "index": "option1"
              },
              {
                "value": found_option.option2,
                "index": "option2"
              }
            ];
          } else {
            return;
          }
        }

        const available_options = this.createAvailableOptionsTree(variant_data, selected_options);

        this.fieldsets.forEach((fieldset, i) => {
          if (!fieldset) return; // Skip if fieldset is null
          
          const fieldset_options = Object.values(available_options)[i];

          if (fieldset_options) {
            if (fieldset.querySelector('select')) {
              fieldset_options.forEach((option, option_i) => {
                if (!option || option.value === null || option.value === undefined) return;
                const optionElement = fieldset.querySelector('option[value=' + JSON.stringify(option.value) + ']');
                if (optionElement) {
                  if (option.isUnavailable) {
                    optionElement.disabled = true;
                  } else {
                    optionElement.disabled = false;
                  }
                }
              });
            } else if (fieldset.querySelectorAll('input').length) {
              // This code has been replaced since the metafield allows you to move the variants around, the input array didn't match the variant array
              //fieldset.querySelectorAll('input').sort().forEach((input, input_i) => {
                //console.log("Option input: ", input, "Input index: ", input_i, fieldset_options[input_i])
                //input.classList.toggle('is-disabled', fieldset_options[input_i].isUnavailable);
              //});
              Array.from(fieldset.querySelectorAll('input'))
              .filter(input => input && input.value) // Filter out null inputs or inputs without value
              .sort((a, b) => {
                if (!a || !b || !a.value || !b.value) return 0;
                return a.value.localeCompare(b.value);
              })
              .forEach(input => {
                if (input && input.value) {
                  const matchingOption = fieldset_options.find(option => option.value === input.value);
                  input.classList.toggle('is-disabled', matchingOption?.isUnavailable || false);
                }
              });
            }
          }
        });

      }
      return true;
    }

    getImageSetName(variant_name) {
      return variant_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-$/, '').replace(/^-/, '');
    }

    setImageSet() {
      if (!this.productSlider) return;

      // Store section ID to validate that product hasn't changed during async operations
      const currentSectionId = this.dataset.section;
      
      let dataSetEl = this.productSlider.querySelector('[data-set-name]');
      if (dataSetEl) {
        this.imageSetName = dataSetEl.dataset.setName;
        const imageSetInput = this.querySelector('.product-form__input[data-handle="' + this.imageSetName + '"]');
        if (imageSetInput && imageSetInput.dataset && imageSetInput.dataset.index) {
          this.imageSetIndex = imageSetInput.dataset.index;
          this.dataset.imageSetIndex = this.imageSetIndex;
        } else {
          // Retry if element not ready yet (race condition with metafield images)
          // Store timeout ID so it can be cancelled if product changes
          this._imageSetTimeout = setTimeout(() => {
            // Validate that product hasn't changed before proceeding
            if (this.dataset.section !== currentSectionId) {
              console.log('[variant-selects] setImageSet: Product changed, cancelling retry');
              return;
            }
            
            const retryInput = this.querySelector('.product-form__input[data-handle="' + this.imageSetName + '"]');
            if (retryInput && retryInput.dataset && retryInput.dataset.index) {
              // Double-check section hasn't changed
              if (this.dataset.section !== currentSectionId) {
                console.log('[variant-selects] setImageSet: Product changed during retry');
                return;
              }
              this.imageSetIndex = retryInput.dataset.index;
              this.dataset.imageSetIndex = this.imageSetIndex;
              if (this.imageSetIndex) {
                this.setImageSetMedia();
              }
            }
          }, 100);
        }
        
        // Log para examinar la configuración del image set
        console.log('[variant-selects] setImageSet:', {
          context: this.closest('#Product-Drawer-Content') ? 'quick-view' : 'product-page',
          section: this.dataset.section,
          imageSetName: this.imageSetName,
          imageSetIndex: this.imageSetIndex,
          hideVariants: this.hideVariants,
          currentVariant: this.currentVariant ? {
            id: this.currentVariant.id,
            title: this.currentVariant.title
          } : null
        });
        
        // Only call setImageSetMedia if imageSetIndex is available
        if (this.imageSetIndex) {
          this.setImageSetMedia();
        }
      } else {
        console.log('[variant-selects] setImageSet: No data-set-name found', {
          context: this.closest('#Product-Drawer-Content') ? 'quick-view' : 'product-page',
          section: this.dataset.section
        });
      }
    }

    setImageSetMedia() {
      if (!this.imageSetIndex) {
        console.log('[variant-selects] setImageSetMedia: No imageSetIndex', {
          context: this.closest('#Product-Drawer-Content') ? 'quick-view' : 'product-page',
          section: this.dataset.section
        });
        return;
      }
      
      // Verify currentVariant and imageSetIndex are valid before accessing
      if (!this.currentVariant || !this.currentVariant[this.imageSetIndex]) {
        console.log('[variant-selects] setImageSetMedia: currentVariant or imageSetIndex value not available', {
          context: this.closest('#Product-Drawer-Content') ? 'quick-view' : 'product-page',
          section: this.dataset.section,
          hasCurrentVariant: !!this.currentVariant,
          imageSetIndex: this.imageSetIndex,
          currentVariantKeys: this.currentVariant ? Object.keys(this.currentVariant) : null
        });
        return;
      }
      
      let setValue = this.getImageSetName(this.currentVariant[this.imageSetIndex]);
      let group = this.imageSetName + '_' + setValue;
      // Buscar tanto en product-images como en product-quick-images
      let selected_set_images = this.productSlider.querySelectorAll(`.product-images__slide[data-set-name="${this.imageSetName}"], .product-quick-images__slide[data-set-name="${this.imageSetName}"]`),
        selected_set_thumbs = this.productWrapper.querySelectorAll(`.product-thumbnail[data-set-name="${this.imageSetName}"]`);
      
      // Log inicial para examinar el filtrado
      console.log('[variant-selects] setImageSetMedia START:', {
        context: this.closest('#Product-Drawer-Content') ? 'quick-view' : 'product-page',
        section: this.dataset.section,
        imageSetName: this.imageSetName,
        imageSetIndex: this.imageSetIndex,
        setValue: setValue,
        group: group,
        hideVariants: this.hideVariants,
        currentVariant: this.currentVariant ? {
          id: this.currentVariant.id,
          title: this.currentVariant.title,
          [this.imageSetIndex]: this.currentVariant[this.imageSetIndex]
        } : null,
        totalImages: selected_set_images.length,
        totalThumbs: selected_set_thumbs.length
      });

      if (this.hideVariants) {
        const imagesByGroup = {};
        const thumbsByGroup = {};
        
        // Contar imágenes por grupo antes de filtrar
        this.productWrapper.querySelectorAll('.product-images__slide, .product-quick-images__slide').forEach(thumb => {
          const thumbGroup = thumb.dataset.group || 'no-group';
          if (!imagesByGroup[thumbGroup]) imagesByGroup[thumbGroup] = [];
          imagesByGroup[thumbGroup].push({
            id: thumb.id,
            mediaId: thumb.dataset.mediaId,
            group: thumbGroup,
            isActive: thumb.classList.contains('is-active')
          });
        });
        
        if (this.thumbnails) {
          this.productWrapper.querySelectorAll('.product-thumbnail').forEach(thumb => {
            const thumbGroup = thumb.dataset.group || 'no-group';
            if (!thumbsByGroup[thumbGroup]) thumbsByGroup[thumbGroup] = [];
            thumbsByGroup[thumbGroup].push({
              id: thumb.id,
              group: thumbGroup,
              isActive: thumb.classList.contains('is-active')
            });
          });
        }
        
        if (this.thumbnails) {
          // Product images (tanto product-images como product-quick-images)
          this.productWrapper.querySelectorAll('.product-images__slide, .product-quick-images__slide').forEach(thumb => {
            if (thumb.dataset.group && thumb.dataset.group !== group) {
              thumb.classList.remove('is-active');
            }
          });
          selected_set_images.forEach(thumb => {
            thumb.classList.toggle('is-active', thumb.dataset.group === group);
          });

          // Product thumbnails
          this.productWrapper.querySelectorAll('.product-thumbnail').forEach(thumb => {
            if (thumb.dataset.group && thumb.dataset.group !== group) {
              thumb.classList.remove('is-active');
            }
          });
          selected_set_thumbs.forEach(thumb => {
            thumb.classList.toggle('is-active', thumb.dataset.group === group);
          });
        } else {
          // Product images (tanto product-images como product-quick-images)
          this.productWrapper.querySelectorAll('.product-images__slide, .product-quick-images__slide').forEach(thumb => {
            if (thumb.dataset.group && thumb.dataset.group !== group) {
              thumb.classList.remove('is-active');
            }
          });
          selected_set_images.forEach(thumb => {
            if (thumb.dataset.group === group) {
              thumb.classList.add('is-active');
            }
          });
        }
        
        // Contar imágenes activas después de filtrar
        const activeImages = Array.from(this.productWrapper.querySelectorAll('.product-images__slide.is-active, .product-quick-images__slide.is-active'));
        const activeThumbs = this.thumbnails ? Array.from(this.productWrapper.querySelectorAll('.product-thumbnail.is-active')) : [];
        
        console.log('[variant-selects] setImageSetMedia FILTERED (hideVariants=true):', {
          context: this.closest('#Product-Drawer-Content') ? 'quick-view' : 'product-page',
          section: this.dataset.section,
          targetGroup: group,
          imagesByGroup: imagesByGroup,
          thumbsByGroup: Object.keys(thumbsByGroup).length > 0 ? thumbsByGroup : null,
          activeImagesCount: activeImages.length,
          activeImages: activeImages.map(img => ({
            id: img.id,
            mediaId: img.dataset.mediaId,
            group: img.dataset.group
          })),
          activeThumbsCount: activeThumbs.length
        });

      } else {

        if (!this.thumbnails) {
          let set_images = Array.from(selected_set_images).filter(function (element) {
            return element.dataset.group === group;
          });
          set_images.forEach(thumb => {
            thumb.parentElement.prepend(thumb);
          });
        }

      }
      if (!this.thumbnails) {
        setTimeout(() => {
          // Add null checks to prevent errors when productSlider or product-slider element doesn't exist
          if (this.productSlider) {
            const productSliderElement = this.productSlider.querySelector('product-slider');
            if (productSliderElement && typeof productSliderElement.onPaginationResize === 'function') {
              productSliderElement.onPaginationResize();
            }
          }
        }, 100);
      }
    }

    createAvailableOptionsTree(variants, currentlySelectedValues) {
      // Reduce variant array into option availability tree
      return variants.reduce((options, variant) => {
        if (!variant) return options; // Skip if variant is null

        // Check each option group (e.g. option1, option2, option3) of the variant
        Object.keys(options).forEach(index => {

          if (variant[index] === null || variant[index] === undefined) return;
          
          // Ensure options[index] exists and is an array
          if (!options[index] || !Array.isArray(options[index])) {
            options[index] = [];
          }

          let entry = options[index].find(option => option && option.value === variant[index]);

          if (typeof entry === 'undefined') {
            // If option has yet to be added to the options tree, add it
            entry = { value: variant[index], isUnavailable: true };
            options[index].push(entry);
          }

          const currentOption1 = currentlySelectedValues && Array.isArray(currentlySelectedValues) 
            ? currentlySelectedValues.find(({ value, index }) => index === 'option1' && value !== null && value !== undefined)
            : null;
          const currentOption2 = currentlySelectedValues && Array.isArray(currentlySelectedValues)
            ? currentlySelectedValues.find(({ value, index }) => index === 'option2' && value !== null && value !== undefined)
            : null;

          switch (index) {
            case 'option1':
              // Option1 inputs should always remain enabled based on all available variants
              entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
              break;
            case 'option2':
              // Option2 inputs should remain enabled based on available variants that match first option group
              if (currentOption1 && variant.option1 === currentOption1.value) {
                entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
              }
              break;
            case 'option3':
              // Option 3 inputs should remain enabled based on available variants that match first and second option group
              if (currentOption1 && variant.option1 === currentOption1.value && currentOption2 && variant.option2 === currentOption2.value) {
                entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
              }
          }
        });

        return options;
      }, { option1: [], option2: [], option3: [] });
    }

    getVariantData() {
      // Get current section ID to validate cache belongs to this product
      const currentSectionId = this.dataset.section;
      
      // Clear cache if section changed (product changed)
      if (this.variantData && this._cachedSectionId !== currentSectionId) {
        this.variantData = null;
        this._cachedSectionId = null;
      }
      
      if (!this.variantData) {
        const jsonElement = this.querySelector('[type="application/json"]');
        if (!jsonElement || !jsonElement.textContent) {
          console.error('[variant-selects] JSON element not found or empty');
          return [];
        }
        try {
          this.variantData = JSON.parse(jsonElement.textContent);
          // Store section ID to validate cache belongs to this product
          this._cachedSectionId = currentSectionId;
          
          // Validate and clean variant data to ensure no null values cause issues
          if (Array.isArray(this.variantData)) {
            this.variantData = this.variantData.map(variant => {
              if (!variant) return variant;
              // Ensure featured_media is properly structured
              if (variant.featured_media === null || variant.featured_media === undefined) {
                variant.featured_media = null;
              }
              return variant;
            });
          }
        } catch (e) {
          console.error('[variant-selects] Error parsing variant data:', e);
          this.variantData = [];
          this._cachedSectionId = currentSectionId;
        }
      }
      return this.variantData;
    }
  }
  customElements.define('variant-selects', VariantSelects);

  /**
   *  @class
   *  @function VariantRadios
   */
  class VariantRadios extends VariantSelects {
    constructor() {
      super();
    }

    updateOptions() {
      const fieldsets = Array.from(this.querySelectorAll('fieldset'));
      this.options = fieldsets.map((fieldset) => {
        if (!fieldset) return null;
        const inputs = Array.from(fieldset.querySelectorAll('input'));
        if (!inputs || inputs.length === 0) return null;
        const checkedRadio = inputs.find((radio) => radio && radio.checked);
        // Additional validation: ensure checkedRadio exists and has value property
        if (!checkedRadio || typeof checkedRadio.value === 'undefined' || checkedRadio.value === null) {
          return null;
        }
        return checkedRadio.value;
      }).filter(option => option !== null);
    }
  }

  customElements.define('variant-radios', VariantRadios);
}

if (!customElements.get('product-slider')) {
  /**
   *  @class
   *  @function ProductSlider
   */
  class ProductSlider extends HTMLElement {
    constructor() {
      super();

    }
    connectedCallback() {
      this.pagination = this.parentElement.querySelector('.product-images-buttons');
      this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
      this.video_containers = this.querySelectorAll('.product-single__media-external-video--play');

      // Start Gallery
      let observer = new MutationObserver(() => {
        this.setupProductGallery();
      });

      observer.observe(this, {
        attributes: true,
        attributeFilter: ['class'],
        childList: true,
        characterData: false
      });

      this.setupProductGallery();

      // Start Pagination
      if (this.pagination) {
        this.setupPagination();
        this.resizeObserver = new ResizeObserver(entries => this.onPaginationResize());
        this.resizeObserver.observe(this);
        this.addEventListener('scroll', this.updatePagination.bind(this));
      }
    }
    setupProductGallery() {
      if (!this.querySelectorAll('.product-single__media-zoom').length) {
        return;
      }

      this.setEventListeners();
    }
    buildItems(activeImages) {
      let images = activeImages.map((item) => {
        let activelink = item.querySelector('.product-single__media-zoom');
        return {
          src: activelink.getAttribute('href'),
          msrc: activelink.dataset.msrc,
          w: activelink.dataset.w,
          h: activelink.dataset.h,
          title: activelink.getAttribute('title')
        };
      });
      return images;
    }
    setEventListeners() {
      let activeImages = Array.from(this.querySelectorAll('.product-images__slide--image')).filter(element => element.clientWidth > 0),
        items = this.buildItems(activeImages),
        captionEl = this.dataset.captions,
        pswpElement = document.querySelectorAll('.pswp')[0],
        options = {
          maxSpreadZoom: 2,
          loop: false,
          allowPanToNext: false,
          closeOnScroll: false,
          showHideOpacity: false,
          arrowKeys: true,
          history: false,
          captionEl: captionEl,
          fullscreenEl: false,
          zoomEl: false,
          shareEl: false,
          counterEl: true,
          arrowEl: true,
          preloaderEl: true
        };

      let openPswp = function (e, link, options, pswpElement, items) {
        let parent = link.closest('.product-images__slide');
        let i = activeImages.indexOf(parent);
        options.index = parseInt(i, 10);
        options.getThumbBoundsFn = () => {
          const thumbnail = link.closest('.product-single__media'),
            pageYScroll = window.scrollY || document.documentElement.scrollTop,
            rect = thumbnail.getBoundingClientRect();
          return {
            x: rect.left,
            y: rect.top + pageYScroll,
            w: rect.width
          };
        };
        if (typeof PhotoSwipe !== 'undefined') {
          let pswp = new PhotoSwipe(pswpElement, PhotoSwipeUI_Default, items, options);

          pswp.listen('firstUpdate', () => {
            pswp.listen('parseVerticalMargin', function (item) {
              item.vGap = {
                top: 50,
                bottom: 50
              };
            });
          });
          pswp.init();
        }
        e.preventDefault();
      };
      this.querySelectorAll('.product-single__media-zoom').forEach(function (link) {
        let thumbnail = link.closest('.product-single__media');
        let clone = link.cloneNode(true);
        thumbnail.append(clone);
        link.remove();
        clone.addEventListener('click', (e) => openPswp(e, clone, options, pswpElement, items));
      });

      this.video_containers.forEach((container) => {
        const button = container.querySelector('button');
        if (button) {
          button.addEventListener('click', function () {
            container.setAttribute('hidden', '');
          });
        }
      });
    }
    setupPagination() {
      this.sliderItemsToShow = Array.from(this.sliderItems).filter(element => element.clientWidth > 0);
      if (this.sliderItemsToShow.length < 2) return;

      this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;

      this.currentPageElement = this.pagination.querySelector('.slider-counter--current');
      this.pageTotalElement = this.pagination.querySelector('.slider-counter--total');

      this.prevButton = this.pagination.querySelector('button[name="previous"]');
      this.nextButton = this.pagination.querySelector('button[name="next"]');


      if (this.prevButton) {
        this.prevButton.addEventListener('click', this.onPaginationButtonClick.bind(this));
      }
      if (this.nextButton) {
        this.nextButton.addEventListener('click', this.onPaginationButtonClick.bind(this));
      }

      this.updatePagination();
    }
    onPaginationResize() {
      this.sliderItemsToShow = Array.from(this.sliderItems).filter(element => element.clientWidth > 0);

      if (this.sliderItemsToShow.length < 2) return;

      this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
      this.updatePagination();
    }
    onPaginationButtonClick(event) {
      event.preventDefault();
      this.slideScrollPosition = event.currentTarget.name === 'next' ? this.scrollLeft + (1 * this.sliderItemOffset) : this.scrollLeft - (1 * this.sliderItemOffset);
      this.scrollTo({
        left: this.slideScrollPosition
      });
    }
    updatePagination() {
      if (!this.nextButton) return;

      const previousPage = this.currentPage;
      this.currentPage = Math.round(this.scrollLeft / this.sliderItemOffset) + 1;

      if (this.currentPageElement) {
        this.currentPageElement.textContent = this.currentPage;
      }
      if (this.pageTotalElement) {
        this.pageTotalElement.textContent = this.sliderItemsToShow.length;
      }

      if (this.currentPage != previousPage) {
        this.dispatchEvent(new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1]
          }
        }));
      }

      if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.scrollLeft === 0) {
        this.prevButton.setAttribute('disabled', 'disabled');
      } else {
        this.prevButton.removeAttribute('disabled');
      }

      if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
        this.nextButton.setAttribute('disabled', 'disabled');
      } else {
        this.nextButton.removeAttribute('disabled');
      }
    }
    isSlideVisible(element, offset = 0) {
      const lastVisibleSlide = this.clientWidth + this.scrollLeft - offset;
      return (element.offsetLeft + element.clientWidth) <= lastVisibleSlide && element.offsetLeft >= this.scrollLeft;
    }
  }
  customElements.define('product-slider', ProductSlider);
  
  // Product Images Loader - Follows Shopify best practices
  // Monitors the LCP (Largest Contentful Paint) image and first visible images
  class ProductImagesLoader {
    constructor() {
      this.sectionId = null;
      this.loadedImages = new Set();
      this.targetImages = [];
      this.maxWaitTime = 2000; // 2 seconds max wait (Shopify recommendation)
      this.startTime = null;
      this.init();
    }
    
    init() {
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setup());
      } else {
        this.setup();
      }
    }
    
    setup() {
      this.startTime = performance.now();
      
      // Find product slider
      const productSlider = document.querySelector('product-slider, .product-images');
      if (!productSlider) {
        // No product slider found, dispatch event immediately
        this.dispatchLoaded();
        return;
      }
      
      // Get section ID from variant-selects or product-form
      const variantSelects = document.querySelector('variant-selects');
      const productForm = document.querySelector('product-form');
      this.sectionId = variantSelects?.dataset.section || productForm?.dataset.section || 'main-product';
      
      // Wait for lazySizes to initialize (if used)
      const checkInterval = setInterval(() => {
        const images = this.findTargetImages(productSlider);
        
        if (images.length > 0 || performance.now() - this.startTime > 500) {
          clearInterval(checkInterval);
          this.monitorImages(images);
        }
      }, 50);
      
      // Fallback: dispatch after max wait time
      setTimeout(() => {
        clearInterval(checkInterval);
        if (this.loadedImages.size < this.targetImages.length) {
          this.dispatchLoaded();
        }
      }, this.maxWaitTime);
    }
    
    findTargetImages(productSlider) {
      // Shopify best practice: Monitor only the LCP image (first visible/active)
      const activeSlide = productSlider.querySelector('.product-images__slide.is-active, .product-images__slide:first-child');
      const firstImage = activeSlide ? activeSlide.querySelector('img') : productSlider.querySelector('img');
      
      const images = [];
      if (firstImage) {
        images.push(firstImage);
      }
      
      // Also monitor the first image in the slider (fallback)
      const allImages = productSlider.querySelectorAll('img');
      if (allImages.length > 0 && !images.includes(allImages[0])) {
        images.push(allImages[0]);
      }
      
      return images.slice(0, 1); // Only monitor the LCP image
    }
    
    monitorImages(images) {
      if (images.length === 0) {
        this.dispatchLoaded();
        return;
      }
      
      this.targetImages = images;
      let loadedCount = 0;
      
      images.forEach((img) => {
        // Check if already loaded
        if (img.complete && img.naturalHeight !== 0) {
          this.onImageLoaded(img);
          loadedCount++;
        } else {
          // Listen for load events (works with lazySizes)
          const handleLoad = () => {
            this.onImageLoaded(img);
            loadedCount++;
            if (loadedCount >= images.length) {
              this.dispatchLoaded();
            }
          };
          
          img.addEventListener('load', handleLoad, { once: true });
          img.addEventListener('error', handleLoad, { once: true });
          
          // If using lazySizes, also listen for lazyloaded event
          if (typeof lazySizes !== 'undefined') {
            img.addEventListener('lazyloaded', handleLoad, { once: true });
          }
        }
      });
      
      // If all images already loaded
      if (loadedCount >= images.length) {
        this.dispatchLoaded();
      }
    }
    
    onImageLoaded(img) {
      if (this.loadedImages.has(img)) return;
      this.loadedImages.add(img);
    }
    
    dispatchLoaded() {
      // Prevent multiple dispatches
      if (this.dispatched) return;
      this.dispatched = true;
      
      const loadTime = performance.now() - this.startTime;
      
      // Dispatch event following Shopify event naming conventions
      document.dispatchEvent(new CustomEvent('product:images-loaded', {
        detail: {
          sectionId: this.sectionId,
          loadedCount: this.loadedImages.size,
          loadTime: Math.round(loadTime)
        },
        bubbles: true,
        cancelable: false
      }));
    }
  }
  
  // Initialize product images loader only on product pages
  if (document.body.classList.contains('template-product') || window.location.pathname.includes('/products/')) {
    new ProductImagesLoader();
  }
}

/**
 *  @class
 *  @function ProductForm
 */
if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      // Bloquear product-add-to-cart-container durante 1 segundo con estilo plomo
      this.initContainerBlock();
      this.sticky = this.dataset.sticky;
      this.form = document.getElementById(`product-form-${this.dataset.section}`);
      if (this.form) {
        const idInput = this.form.querySelector('[name=id]');
        if (idInput) {
          idInput.disabled = false;
        }
        if (!this.sticky) {
          this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        }
      }
      this.cartNotification = document.querySelector('cart-notification');
      this.body = document.body;

      this.hideErrors = this.dataset.hideErrors === 'true';
      
      // Lock button until product images are loaded
      this.initButtonLock();
    }
    
    initButtonLock() {
      const submitButtons = document.querySelectorAll('.single-add-to-cart-button');
      const sectionId = this.dataset.section;
      
      // Shopify best practice: Only disable if truly necessary (not for sold out items)
      submitButtons.forEach((button) => {
        const isSoldOut = button.classList.contains('sold-out');
        if (!isSoldOut && !button.hasAttribute('disabled')) {
          button.setAttribute('disabled', 'disabled');
          button.setAttribute('data-waiting-images', 'true');
          button.classList.add('button-loading'); // Gray state
          button.setAttribute('aria-busy', 'true'); // Accessibility
        }
      });
      
      // Listen for product images loaded event (Shopify event pattern)
      const handleImagesLoaded = (e) => {
        // Match section ID or allow if no section ID specified (global)
        if (e.detail.sectionId && e.detail.sectionId !== sectionId) {
          return;
        }
        
        submitButtons.forEach((button) => {
          if (button.hasAttribute('data-waiting-images')) {
            const isSoldOut = button.classList.contains('sold-out');
            if (!isSoldOut) {
              // Remove loading state and add shine animation
              button.classList.remove('button-loading');
              button.classList.add('button-loaded');
              button.removeAttribute('disabled');
              button.removeAttribute('data-waiting-images');
              button.removeAttribute('aria-busy');
              
              // Remove shine class after animation completes
              setTimeout(() => {
                button.classList.remove('button-loaded');
              }, 600);
            } else {
              button.removeAttribute('data-waiting-images');
              button.removeAttribute('aria-busy');
            }
          }
        });
        
        // Remove listener after first successful load
        document.removeEventListener('product:images-loaded', handleImagesLoaded);
      };
      
      document.addEventListener('product:images-loaded', handleImagesLoaded);
      
      // Fallback: Enable button after max wait time (2 seconds - Shopify recommendation)
      setTimeout(() => {
        submitButtons.forEach((button) => {
          if (button.hasAttribute('data-waiting-images')) {
            const isSoldOut = button.classList.contains('sold-out');
            if (!isSoldOut) {
              button.classList.remove('button-loading');
              button.removeAttribute('disabled');
              button.removeAttribute('data-waiting-images');
              button.removeAttribute('aria-busy');
            }
          }
        });
        document.removeEventListener('product:images-loaded', handleImagesLoaded);
      }, 2000);
    }
    
    initContainerBlock() {
      // Buscar todos los contenedores product-add-to-cart-container
      const containers = document.querySelectorAll('.product-add-to-cart-container');
      
      containers.forEach((container) => {
        // Aplicar estado de carga (plomo/gris)
        container.classList.add('container-loading');
        
        // Después de 1 segundo, remover el bloqueo y aplicar estado cargado
        setTimeout(() => {
          container.classList.remove('container-loading');
          container.classList.add('container-loaded');
          
          // Remover la clase loaded después de la transición para permitir futuras transiciones
          setTimeout(() => {
            container.classList.remove('container-loaded');
          }, 300); // Después de la transición CSS (0.3s)
        }, 1000); // 1 segundo de bloqueo
      });
    }
    
    onSubmitHandler(evt) {
      evt.preventDefault();
      if (!this.form.reportValidity()) {
        return;
      }
      
      // Verify variant ID is available before submitting
      const variantIdInput = this.form.querySelector('input[name="id"]');
      const variantId = variantIdInput ? variantIdInput.value : null;
      
      if (!variantId || variantId === 'undefined' || variantId === 'null') {
        console.error('[Product Form] Cannot submit: variant ID not ready', {
          variantId: variantId,
          form: this.form
        });
        // Try to get variant from variant-selects
        const variantSelects = this.form.closest('.product-form')?.previousElementSibling?.querySelector('variant-selects');
        if (variantSelects && variantSelects.currentVariant && variantSelects.currentVariant.id) {
          if (variantIdInput) {
            variantIdInput.value = variantSelects.currentVariant.id;
            variantIdInput.removeAttribute('disabled');
          }
        } else {
          console.error('[Product Form] Variant not available, preventing submission');
          return;
        }
      }
      
      const submitButtons = document.querySelectorAll('.single-add-to-cart-button');

      // Check if already submitting to prevent double-submit on mobile
      let isSubmitting = false;
      submitButtons.forEach((submitButton) => {
        if (submitButton.classList.contains('loading') || submitButton.disabled) {
          isSubmitting = true;
        }
      });
      if (isSubmitting) {
        return;
      }

      submitButtons.forEach((submitButton) => {
        submitButton.setAttribute('aria-disabled', true);
        submitButton.classList.add('loading');
        submitButton.disabled = true;
      });

      this.handleErrorMessage();


      const config = {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/javascript'
        }
      };
      let formData = new FormData(this.form);

      formData.append('sections', this.getSectionsToRender().map((section) => section.section));
      formData.append('sections_url', window.location.pathname);
      config.body = formData;

      fetch(`${theme.routes.cart_add_url}`, config)
        .then((response) => {
          if (!response.ok) {
            return response.json().then((errorData) => {
              throw new Error(errorData.description || errorData.message || 'Failed to add item to cart');
            });
          }
          return response.json();
        })
        .then((response) => {
          if (response.status) {
            dispatchCustomEvent('product:variant-error', {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: response.description,
              message: response.message
            });
            if (response.status === 422) {
              document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
                bubbles: true
              }));
            }
            console.error('[Product Form] Add to cart failed:', {
              status: response.status,
              message: response.description || response.message || 'This item is unavailable. Please try a different option.',
              response: response,
              variantId: formData.get('id')
            });
            return;
          }

          this.renderContents(response);

          dispatchCustomEvent('cart:item-added', {
            product: response.hasOwnProperty('items') ? response.items[0] : response
          });
        })
        .catch((e) => {
          console.error('[Product Form] Add to cart error:', {
            error: e,
            message: e.message,
            variantId: formData.get('id')
          });
        })
        .finally(() => {
          submitButtons.forEach((submitButton) => {
            submitButton.classList.remove('loading');
            submitButton.removeAttribute('aria-disabled');
            submitButton.disabled = false;
            // Re-enable after a short delay to prevent rapid double-taps on mobile
            setTimeout(() => {
              if (submitButton && !submitButton.hasAttribute('disabled')) {
                submitButton.disabled = false;
              }
            }, 300);
          });
        });
    }

    getSectionsToRender() {
      return [{
        id: 'Cart',
        section: 'main-cart',
        selector: '.thb-cart-form'
      },
      {
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }];
    }
    renderContents(parsedState) {
      this.getSectionsToRender().forEach((section => {
        if (!document.getElementById(section.id)) {
          return;
        }
        const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
        elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);

        if (section.id === 'Cart-Drawer') {
          document.getElementById('Cart-Drawer')?.notesToggle();
          document.getElementById('Cart-Drawer')?.removeProductEvent();
        }
        if (section.id === 'Cart' && typeof Cart !== 'undefined') {
          new Cart().renderContents(parsedState);
        }
      }));



      let product_drawer = document.getElementById('Product-Drawer');
      if (product_drawer && product_drawer.contains(this)) {
        product_drawer.querySelector('.product-quick-images--container').classList.remove('active');
        document.body.classList.remove('open-quick-view');

        if (window.innerWidth < 1069) {
          product_drawer.classList.remove('active');
          if (document.getElementById('Cart-Drawer')) {
            const cartDrawer = document.getElementById('Cart-Drawer');
            cartDrawer.classList.add('active');
            document.body.classList.add('open-cart');
            document.body.classList.add('open-cc');
            const recommendations = cartDrawer.querySelector('.product-recommendations--full');
            if (recommendations) {
              recommendations.classList.add('active');
            }
            dispatchCustomEvent('cart-drawer:open');
          }
        } else {
          const quickImagesContainer = product_drawer.querySelector('.product-quick-images--container');
          if (quickImagesContainer) {
            quickImagesContainer.addEventListener('transitionend', function () {
              product_drawer.classList.remove('active');

              if (document.getElementById('Cart-Drawer')) {
                const cartDrawer = document.getElementById('Cart-Drawer');
                cartDrawer.classList.add('active');
                document.body.classList.add('open-cart');
                document.body.classList.add('open-cc');
                const recommendations = cartDrawer.querySelector('.product-recommendations--full');
                if (recommendations) {
                  recommendations.classList.add('active');
                }
                dispatchCustomEvent('cart-drawer:open');
              }
            });
          }
        }

        if (!document.getElementById('Cart-Drawer')) {
          document.body.classList.remove('open-cc');
        }
      } else if (document.getElementById('Cart-Drawer')) {
        document.body.classList.add('open-cc');
        document.body.classList.add('open-cart');
        document.getElementById('Cart-Drawer').classList.add('active');
        dispatchCustomEvent('cart-drawer:open');
      }
    }
    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }
    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;
      
      if (errorMessage) {
        console.error('[Product Form] Error message:', {
          message: errorMessage,
          section: this.dataset.section
        });
      }
      
      // Hide error message wrapper if it exists (don't show to user)
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (this.errorMessageWrapper) {
        this.errorMessageWrapper.setAttribute('hidden', true);
      }
    }
  });
}

/**
 *  @class
 *  @function ProductAddToCartSticky
 */
if (!customElements.get('product-add-to-cart-sticky')) {
  class ProductAddToCartSticky extends HTMLElement {
    constructor() {
      super();

      this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';
    }
    connectedCallback() {
      this.setupObservers();
      this.setupToggle();
    }
    setupToggle() {
      const button = this.querySelector('.product-add-to-cart-sticky--inner'),
        content = this.querySelector('.product-add-to-cart-sticky--content');

      if (!button || !content) {
        return;
      }

      if (this.animations_enabled) {
        const tl = gsap.timeline({
          reversed: true,
          paused: true,
          onStart: () => {
            button.classList.add('sticky-open');
          },
          onReverseComplete: () => {
            button.classList.remove('sticky-open');
          }
        });

        tl
          .set(content, {
            display: 'block',
            height: 'auto'
          }, 'start')
          .from(content, {
            height: 0,
            duration: 0.25
          }, 'start+=0.001');

        button.addEventListener('click', function () {
          tl.reversed() ? tl.play() : tl.reverse();

          return false;
        });
      } else {
        button.addEventListener('click', function () {
          content.classList.toggle('active');
          return false;
        });
      }


    }
    setupObservers() {
      let _this = this,
        observer = new IntersectionObserver(function (entries) {
          entries.forEach((entry) => {
            if (entry.target === footer) {
              if (entry.intersectionRatio > 0) {
                _this.classList.remove('sticky--visible');
              } else if (entry.intersectionRatio == 0 && _this.formPassed) {
                _this.classList.add('sticky--visible');
              }
            }
            if (entry.target === form) {
              let boundingRect = form.getBoundingClientRect();

              if (entry.intersectionRatio === 0 && window.scrollY > (boundingRect.top + boundingRect.height)) {
                _this.formPassed = true;
                _this.classList.add('sticky--visible');
              } else if (entry.intersectionRatio === 1) {
                _this.formPassed = false;
                _this.classList.remove('sticky--visible');
              }
            }
          });
        }, {
          threshold: [0, 1]
        }),
        form = document.getElementById(`product-form-${this.dataset.section}`),
        footer = document.getElementById('footer');
      _this.formPassed = false;
      observer.observe(form);
      observer.observe(footer);
    }
  }

  customElements.define('product-add-to-cart-sticky', ProductAddToCartSticky);
}

if (typeof addIdToRecentlyViewed !== "undefined") {
  addIdToRecentlyViewed();
}

//nueva prueba
